import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { services } from '../../services';
import contractAddresses from '../../contractData/contractAddress/addresses';
import tokenABI from '../../contractData/abis/token.json';

export interface TransferTx {
  txHash: string;
  toAddress: string;
  amount: number;
  timestamp: number;
}

interface SendModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  senderAddress: string;
  onSuccess?: (tx: TransferTx) => void;
}

/**
 * Converts human-readable token amount to wei (smallest unit) using token decimals.
 * Uses string/BigInt to avoid Number precision issues for large amounts.
 */
function humanToWei(amountHuman: number, decimals: number): string {
  const [whole = '0', frac = ''] = String(amountHuman).split('.');
  const paddedFrac = frac.padEnd(decimals, '0').slice(0, decimals);
  return BigInt(whole + paddedFrac).toString();
}

export function SendModal({ open, onOpenChange, senderAddress, onSuccess }: SendModalProps) {
  const { toast } = useToast();
  const [toAddress, setToAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingMax, setIsLoadingMax] = useState(false);

  const handleMaxClick = async () => {
    const addr = (senderAddress || '').trim();
    if (!addr || !/^0x[a-fA-F0-9]{40}$/.test(addr)) {
      toast({
        title: 'Error',
        description: 'Sender address is required to fetch max balance.',
        variant: 'destructive',
      });
      return;
    }
    setIsLoadingMax(true);
    try {
      const { token: tokenAddress } = contractAddresses();
      const tokenContract = await services.getContractInstance(
        tokenABI as import('web3-utils').AbiItem[],
        tokenAddress
      );
      const [rawBalance, decimalsResult] = await Promise.all([
        tokenContract.methods.balanceOf(addr).call(),
        tokenContract.methods.decimals().call(),
      ]);
      const decimals = Number(decimalsResult ?? 18);
      const rawStr = String(rawBalance ?? '0');
      if (rawStr === '0') {
        setAmount('0');
        return;
      }
      const rawBig = BigInt(rawStr);
      const divisor = BigInt(10 ** decimals);
      const whole = rawBig / divisor;
      const frac = rawBig % divisor;
      const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
      const maxDecimals = 2;
      const fracTrimmed = fracStr.slice(0, maxDecimals).replace(/0+$/, '');
      const display = fracTrimmed ? `${whole}.${fracTrimmed}` : whole.toString();
      setAmount(display);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: 'Could not load balance',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingMax(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTo = toAddress.trim();
    const amountNum = Number(amount);

    if (!trimmedTo) {
      toast({
        title: 'Error',
        description: 'Receiver address is required',
        variant: 'destructive',
      });
      return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(trimmedTo)) {
      toast({
        title: 'Error',
        description: 'Invalid wallet address (must be 0x + 40 hex characters)',
        variant: 'destructive',
      });
      return;
    }
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      toast({
        title: 'Error',
        description: 'Amount must be a positive number',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      let web3Data = await services.getWeb3();
      let accounts = web3Data?.accounts ?? [];
      if (accounts.length === 0) {
        const enabled = await services.enableMetamask();
        if (!enabled) {
          toast({
            title: 'Wallet not connected',
            description:
              'Please connect your wallet (e.g. MetaMask) with the admin wallet to send.',
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }
        web3Data = await services.getWeb3();
        accounts = web3Data?.accounts ?? [];
      }
      const fromAddress = accounts[0];
      if (!fromAddress) {
        toast({
          title: 'Error',
          description: 'No connected wallet. Connect the admin wallet and try again.',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      // Ensure the connected wallet is the admin wallet so the balance shown actually decreases
      const adminAddr = (senderAddress || '').trim().toLowerCase();
      if (adminAddr && fromAddress.toLowerCase() !== adminAddr) {
        toast({
          title: 'Wrong wallet connected',
          description: `Connected wallet (${fromAddress.slice(0, 8)}...) is not the Admin wallet. Switch MetaMask to the admin wallet so the balance above will decrease when you send.`,
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      const { token: tokenAddress } = contractAddresses();
      const tokenContract = await services.getContractInstance(
        tokenABI as import('web3-utils').AbiItem[],
        tokenAddress
      );
      const decimals = Number(await tokenContract.methods.decimals().call()) || 18;
      const amountWei = humanToWei(amountNum, decimals);
      if (BigInt(amountWei) <= BigInt(0)) {
        toast({
          title: 'Error',
          description: 'Amount too small',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      const receipt = await tokenContract.methods
        .transfer(trimmedTo, amountWei)
        .send({ from: fromAddress })
        .on('transactionHash', (hash: string) => {
          toast({
            title: 'Transaction submitted',
            description: `Tx: ${hash.slice(0, 10)}...`,
          });
        });

      const txHash =
        typeof receipt === 'object' && receipt?.transactionHash
          ? String(receipt.transactionHash)
          : '';
      toast({
        title: 'Transfer successful',
        description: txHash
          ? `Transaction: ${txHash.slice(0, 10)}... Balance will update in a few seconds.`
          : 'Tokens sent. Balance will update in a few seconds.',
      });
      setToAddress('');
      setAmount('');
      onOpenChange(false);
      if (txHash) {
        onSuccess?.({
          txHash,
          toAddress: trimmedTo,
          amount: amountNum,
          timestamp: Date.now(),
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: 'Transfer failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Send tokens</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='sender'>Sender (admin wallet)</Label>
            <Input
              id='sender'
              value={senderAddress}
              readOnly
              className='font-mono text-sm bg-muted/50'
            />
            <p className='text-xs text-muted-foreground'>
              Connect with this wallet in MetaMask to sign the transaction.
            </p>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='toAddress'>Receiver address</Label>
            <Input
              id='toAddress'
              placeholder='0x...'
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              className='font-mono'
              disabled={isSubmitting}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='amount'>Amount</Label>
            <div className='flex gap-2'>
              <Input
                id='amount'
                type='number'
                min='0'
                step='any'
                placeholder='0.00'
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={isSubmitting}
                className='flex-1'
              />
              <Button
                type='button'
                variant='outline'
                onClick={handleMaxClick}
                disabled={isSubmitting || isLoadingMax}
              >
                {isLoadingMax ? <Loader2 className='w-4 h-4 animate-spin' /> : 'Max'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className='w-4 h-4 mr-2 animate-spin' />
              ) : (
                <ArrowUpRight className='w-4 h-4 mr-2' />
              )}
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
